import { Button } from '@compass/Button'
import {
  SelectItem,
  SelectListBox,
  SelectPopover,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Typography,
} from '@compass/components'
import { Paper } from '@compass/Paper'
import { Tooltip } from '@compass/Tooltip'
import { cn } from '@compass/utils'
import { Group } from '@visx/group'
import { TimelineExportButton } from 'components/ChartExport'
import { GenericError } from 'const'
import { curveMonotoneX } from 'd3-shape'
import {
  ChartError,
  ChartGraph,
  ChartHeader,
  ChartLayout,
  ChartLoading,
  ChartNoData,
  ChartTitle,
  CircleMark,
  formatChartTimestamp,
  GranularitySelector,
  IncompleteLineWithShadow,
  ResponsiveVisXChart,
  SERIES_COLORS,
  TooltipContainer,
  TooltipContent,
  TooltipHeader,
  useTimelineIndexer,
} from 'features/chart'
import { formatNum, formatNumShort } from 'helpers/format'
import { useCurrentUser } from 'hooks/api/users'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Select, TooltipTrigger } from 'react-aria-components'

import { BOT_CATEGORY_LABELS } from '../consts'
import { BotDetectionTimelineChartId, BotDetectionTimelinePeriod, BotDetectionTimelineResponse } from '../models'

const TIMELINE_CHART_OPTIONS: {
  key: BotDetectionTimelineChartId
  label: string
  info: string
  /** Shown when the response holds no non-zero values. Phrased to hold whether or not filters are applied. */
  emptyMessage: string
  exportColumns?: Record<string, string>
}[] = [
  {
    key: 'totalDetections',
    label: 'Bots by category',
    info: 'Shows detected bots over time, split by category.',
    emptyMessage: 'No bots detected in this period',
  },
  {
    key: 'aiTrafficShare',
    label: 'AI agents by provider',
    info: 'Shows AI bot traffic over time, split by provider.',
    emptyMessage: 'No AI bot traffic in this period',
  },
  {
    key: 'trustedBotShare',
    label: 'Trusted bots',
    info: 'Shows trusted bot detections over time, split by identity.',
    emptyMessage: 'No trusted bots detected in this period',
  },
  {
    key: 'totalBots',
    label: 'Total bots',
    info: 'Shows total events and detected bots over time.',
    emptyMessage: 'No events in this period',
    exportColumns: { totalEvents: 'total_events', detectedBots: 'detected_bots' },
  },
  {
    key: 'spoofedBotCount',
    label: 'Spoofed bots',
    info: 'Shows spoofed bot detections over time.',
    emptyMessage: 'No spoofed bots detected in this period',
    exportColumns: { spoofed: 'spoofed_bots' },
  },
  {
    key: 'unknownBotCount',
    label: 'Unknown bots',
    info: 'Shows unknown bot detections over time.',
    emptyMessage: 'No unknown bots detected in this period',
    exportColumns: { unknown: 'unknown_bots' },
  },
]

type TimelinePoint = {
  x: number
  values: Record<string, number | null>
}

type TimelineSeries = BotDetectionTimelineResponse['series'][number] & {
  color: (typeof SERIES_COLORS)[number]
  total: number
  enabled: boolean
}

export function BotDetectionTimelineChart({
  data,
  chart,
  granularity,
  isLoading,
  error,
  onChartChange,
  onGranularityChange,
  filtersApplied,
}: {
  data?: BotDetectionTimelineResponse
  chart: BotDetectionTimelineChartId
  granularity: BotDetectionTimelinePeriod
  isLoading?: boolean
  error?: GenericError | null
  onChartChange: (chart: BotDetectionTimelineChartId) => void
  onGranularityChange: (granularity: BotDetectionTimelinePeriod) => void
  filtersApplied?: boolean
}) {
  const { data: currentUser } = useCurrentUser()
  const selectedOption = TIMELINE_CHART_OPTIONS.find((option) => option.key === chart) ?? TIMELINE_CHART_OPTIONS[0]
  const timelineData = useTimelineData(data, chart)
  const [series, setSeries] = useState<TimelineSeries[]>(() =>
    timelineData.series.map((item) => ({ ...item, enabled: true }))
  )

  useEffect(() => {
    setSeries(timelineData.series.map((item) => ({ ...item, enabled: true })))
  }, [timelineData.series])

  const onSeriesSelect = useCallback((key: string) => {
    setSeries((prev) => {
      const enabledCount = prev.filter((item) => item.enabled).length

      return prev.map((item) => {
        if (item.key !== key) {
          return item
        }

        const nextEnabled = !item.enabled

        if (!nextEnabled && enabledCount === 1) {
          return { ...item, enabled: true }
        }

        return { ...item, enabled: nextEnabled }
      })
    })
  }, [])

  const { timeline, xDomain, yDomain } = timelineData
  const visibleSeries = useMemo(() => series.filter((item) => item.enabled), [series])
  const hasData = series.some((item) => item.data.some(([, value]) => value > 0))
  const { handlePointer, tooltipIndex, position } = useTimelineIndexer(timeline, (point) => point?.x ?? 0)
  const tooltipData = tooltipIndex != null ? timeline[tooltipIndex] : undefined
  const exportColumns = timelineData.series.map((item) => ({
    key: item.key,
    column: selectedOption.exportColumns?.[item.key] ?? item.label,
  }))

  return (
    <Paper elevation={0} className='relative border-0 rounded-t-none'>
      <ChartLayout>
        <ChartHeader>
          <ChartTitle
            title={<TimelineChartSelector selectedChart={chart} onSelectionChange={onChartChange} />}
            info={selectedOption.info}
            action={
              <div className='ml-auto flex items-center gap-2'>
                <GranularitySelector value={granularity} onChange={onGranularityChange} hidden={{ month: true }} />
                <TimelineExportButton
                  page='bot-detection'
                  widget={chart}
                  granularity={granularity}
                  columns={exportColumns.map(({ column }) => column)}
                  getPoints={() =>
                    timeline.map((point) => ({
                      timestamp: DateTime.fromMillis(point.x, { zone: 'utc' }),
                      values: Object.fromEntries(exportColumns.map(({ key, column }) => [column, point.values[key]])),
                    }))
                  }
                  hasData={!isLoading && !error && hasData}
                  filtersApplied={filtersApplied}
                  // The bot timeline endpoint buckets in UTC regardless of the user's timezone.
                  timeZone='UTC'
                />
              </div>
            }
          />
        </ChartHeader>

        <TimelineLegend series={series} isLoading={isLoading} onSeriesSelect={onSeriesSelect} />

        <ChartGraph>
          {isLoading ? <ChartLoading /> : null}
          {error ? <ChartError /> : null}
          {!isLoading && !error && !hasData ? <ChartNoData message={selectedOption.emptyMessage} /> : null}
          {!isLoading && !error && hasData ? (
            <ResponsiveVisXChart
              timezone={currentUser?.timezone}
              xDomain={xDomain}
              yDomain={yDomain}
              margin={{ top: 16, right: 12, bottom: 20, left: 32 }}
              svgClassName='overflow-visible'
              granularity={granularity}
              handlePointer={handlePointer}
              values={timeline}
            >
              {({ xScale, yScale, xMax, yMax }) => (
                <>
                  <Group clipPath='url(#content)'>
                    {visibleSeries
                      .slice()
                      .reverse()
                      .map((item) => (
                        <IncompleteLineWithShadow
                          key={item.key}
                          xScale={xScale}
                          yScale={yScale}
                          xMax={xMax}
                          yMax={yMax}
                          data={item.data}
                          curve={curveMonotoneX}
                          x={(point) => xScale(point[0])}
                          y={(point) => yScale(point[1])}
                          className={item.color.text}
                          stroke='currentColor'
                          strokeWidth={1.75}
                          strokeOpacity={1}
                          data-testid={`bot-detection-timeline-series-${item.key}`}
                        />
                      ))}
                  </Group>

                  {tooltipData
                    ? visibleSeries.map((item) => {
                        const value = tooltipData.values[item.key]

                        return value != null ? (
                          <CircleMark
                            key={item.key}
                            cx={xScale(tooltipData.x)}
                            cy={yScale(value)}
                            fill={item.color.fill}
                          />
                        ) : null
                      })
                    : null}
                </>
              )}
            </ResponsiveVisXChart>
          ) : null}

          {tooltipData ? (
            <TooltipContainer position={position}>
              <TooltipHeader className='font-mono'>
                {formatChartTimestamp(DateTime.fromMillis(tooltipData.x), granularity)}
              </TooltipHeader>
              <TooltipContent className='grid grid-cols-[auto_1fr_auto] gap-x-2 gap-y-2 items-center justify-between'>
                {visibleSeries.map((item) => {
                  const value = tooltipData.values[item.key]

                  return value != null ? (
                    <div key={item.key} className='contents'>
                      <span className={cn('size-2 rounded-xs border', item.color.bg, item.color.border)} />
                      <span className='text-gray-800'>{item.label}</span>
                      <span className='ml-4 font-mono font-medium text-right'>{formatNum(value)}</span>
                    </div>
                  ) : null
                })}
              </TooltipContent>
            </TooltipContainer>
          ) : null}
        </ChartGraph>
      </ChartLayout>
    </Paper>
  )
}

type BaseTimelineSeries = Omit<TimelineSeries, 'enabled'>

function useTimelineData(data: BotDetectionTimelineResponse | undefined, chart: BotDetectionTimelineChartId) {
  return useMemo(() => {
    const responseSeries = data?.series ?? []
    const series = responseSeries.map<BaseTimelineSeries>((item, index) => {
      const label =
        chart === 'totalDetections' && item.key in BOT_CATEGORY_LABELS
          ? BOT_CATEGORY_LABELS[item.key as keyof typeof BOT_CATEGORY_LABELS]
          : chart === 'totalBots'
            ? item.key === 'totalEvents'
              ? 'Total events'
              : 'Detected bots'
            : item.label

      return {
        ...item,
        label,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        total: item.data.reduce((sum, [, value]) => sum + value, 0),
      }
    })
    const timestamps = Array.from(new Set(series.flatMap((item) => item.data.map(([timestamp]) => timestamp)))).sort(
      (left, right) => left - right
    )
    const valueMaps = new Map(series.map((item) => [item.key, new Map(item.data)]))
    const timeline = timestamps.map<TimelinePoint>((timestamp) => ({
      x: timestamp,
      values: Object.fromEntries(series.map((item) => [item.key, valueMaps.get(item.key)?.get(timestamp) ?? null])),
    }))
    const maxValue = Math.max(0, ...series.flatMap((item) => item.data.map(([, value]) => value)))
    const xDomain: [number, number] =
      timestamps.length > 0
        ? [timestamps[0], timestamps[timestamps.length - 1]]
        : [DateTime.now().minus({ days: 7 }).valueOf(), DateTime.now().valueOf()]
    const yDomain: [number, number] = [0, Math.max(10, Math.ceil(maxValue * 1.12))]

    return { series, timeline, xDomain, yDomain }
  }, [chart, data])
}

function TimelineChartSelector({
  selectedChart,
  onSelectionChange,
}: {
  selectedChart: BotDetectionTimelineChartId
  onSelectionChange: (selected: BotDetectionTimelineChartId) => void
}) {
  return (
    <Select<(typeof TIMELINE_CHART_OPTIONS)[number]>
      aria-label='Change displayed timeline chart'
      selectedKey={selectedChart}
      onSelectionChange={(key) => onSelectionChange(key as BotDetectionTimelineChartId)}
      className='-ml-2'
    >
      <TooltipTrigger delay={750}>
        <Tooltip>Change displayed timeline</Tooltip>
        <SelectTrigger className='border-none shadow-none text-base' size='sm' tone='ghost' fullWidth>
          <SelectValue<(typeof TIMELINE_CHART_OPTIONS)[number]> className='flex flex-col text-left'>
            {({ selectedItem }) => selectedItem?.label ?? <Skeleton className='h-5 w-40 transform-none!' />}
          </SelectValue>
        </SelectTrigger>
      </TooltipTrigger>
      <SelectPopover>
        <SelectListBox>
          {TIMELINE_CHART_OPTIONS.map((option) => (
            <SelectItem key={option.key} id={option.key} value={option} isDisabled={option.key === selectedChart}>
              {option.label}
            </SelectItem>
          ))}
        </SelectListBox>
      </SelectPopover>
    </Select>
  )
}

function TimelineLegend({
  series,
  isLoading,
  onSeriesSelect,
}: {
  series: TimelineSeries[]
  isLoading?: boolean
  onSeriesSelect: (key: string) => void
}) {
  if (series.length === 0) {
    return null
  }

  return (
    <div className='grid grid-cols-2 sm:grid-cols-3 lg:flex flex-wrap justify-end gap-1 -mx-2'>
      {series.map((item) => (
        <div className='flex flex-col items-start lg:items-end text-xs text-gray-800' key={item.key}>
          <Button size='sm' variant='ghost' onClick={() => onSeriesSelect(item.key)}>
            <span
              aria-hidden='true'
              className={cn('size-2 rounded-xs', item.enabled && item.color.bg, `border ${item.color.border}`)}
            />
            <Typography size='sm' variant='body' className='text-gray-800'>
              {item.label}
            </Typography>
          </Button>
          <div style={{ paddingRight: '9px' }} className='pl-6 lg:pl-0'>
            {isLoading ? (
              <Skeleton className='w-10 h-4' />
            ) : (
              <Typography
                size='sm'
                variant='body'
                weight='medium'
                className={`font-mono ${!item.enabled ? 'text-gray-600' : undefined}`}
                title={item.total.toLocaleString()}
              >
                {formatNumShort(item.total, item.total >= 1_000_000 ? 2 : 0)}
              </Typography>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
